import { inject, Injectable } from '@angular/core';
import { DateRange } from '@angular/material/datepicker';
import { DatesDetailsType, DepartureReturnButtonState, DepartureReturnDetailType } from '@pc/departure-return-button';
import { PcDateUtils } from '@pc/shared';
import { TranslationService } from '@pc/translation';
import { CalendarRestrictionsResponse, ProductInfoInterface } from '../model/calendar-restrictions.model';
import { TripDatesState } from '../store/trip-dates.store';
import { map, Observable } from 'rxjs';
import { DestinationsStore } from '../../where-are-you-travelling/store/destinations.store';
import { ProcessTypeStore } from '../../../../store/process-type-store/process-type-store';
import { RestApiService } from '@pc/services';

@Injectable({
  providedIn: 'root'
})
export class TripDatesService {
  private dateUtils = inject(PcDateUtils);
  private translationService = inject(TranslationService);
  private agentStore = inject(ProcessTypeStore);
  private destinationsStore = inject(DestinationsStore);
  private readonly restApiService = inject(RestApiService<CalendarRestrictionsResponse>);

  public getLocaleDate(
    dates: DateRange<Date> | null | undefined,
    isStart = true
  ): DepartureReturnDetailType | undefined {
    const defLanguage = this.translationService.getDefaultLang();

    let localeDate: string | undefined = undefined;
    let date: Date | null | undefined = null;

    date = isStart ? dates?.start : dates?.end;

    const formatOptions: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    };

    localeDate = date?.toLocaleDateString(defLanguage, formatOptions);

    if (localeDate) {
      const [weekDay, date, month] = localeDate.split(' ');
      const fullDate = [date, month].join(' ');
      // for daily policy, the formant is weekDay(Sun, Mon, Tue, etc) and month day(01, 02, 03, etc)
      // for yearly policy, the format is month day(01, 02, 03, etc) and year(2021, 2022, 2023, etc)
      return {
        weekDay: weekDay,
        monthDay: fullDate,
        year: ''
      };
    } else {
      return undefined;
    }
  }

  public getDatesDetails(dates: DateRange<Date> | null | undefined): DatesDetailsType | undefined {
    const departureData = this.getLocaleDate(dates, true);
    const returnData = this.getLocaleDate(dates, false);

    return {
      departure: departureData,
      return: returnData
    };
  }

  public clearDateRange(): DateRange<Date> {
    return new DateRange<Date>(null, null);
  }

  public clearEndDate(dates: DateRange<Date>): DateRange<Date> {
    return new DateRange<Date>(dates?.start, null);
  }

  public calculateTripLength(startDate: Date | null | undefined, endDate: Date | null | undefined): number {
    if (!startDate || !endDate) {
      return 0;
    }

    const days = this.dateUtils.getDatesDifferenceInDays(startDate, endDate);
    return days + 1;
  }

  public calculateDatesDiff(startDate: Date | null | undefined, endDate: Date | null | undefined): number {
    if (!startDate || !endDate) {
      return 0;
    }
    return Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  }

  public calculateTripMonthsLength(startDate: Date | null | undefined, endDate: Date | null | undefined): number {
    if (!startDate || !endDate) {
      return 0;
    }
    return endDate.getMonth() - startDate.getMonth() + 12 * (endDate.getFullYear() - startDate.getFullYear());
  }

  public validateSelectedDates(selectedDates: DateRange<Date>, startDateLimit = -1): Partial<TripDatesState> {
    let result: Partial<TripDatesState> = {};

    if (selectedDates?.end) {
      result = {
        dates: selectedDates,
        datesButtonState: DepartureReturnButtonState.ReturnCompleted,
        datesButtonActiveState: DepartureReturnButtonState.None,
        isValid: true
      };
    } else if (selectedDates?.start && startDateLimit >= 0) {
      const maxStartDate = new Date(selectedDates?.start);
      maxStartDate.setDate(maxStartDate.getDate() + startDateLimit);
      const dateDiff = this.calculateDatesDiff(selectedDates?.start, maxStartDate);

      if (dateDiff >= 0) {
        result = {
          dates: selectedDates,
          datesButtonState: DepartureReturnButtonState.Return,
          datesButtonActiveState: DepartureReturnButtonState.Return,
          isValid: false
        };
      }
    } else if (selectedDates?.start) {
      result = {
        dates: selectedDates,
        datesButtonState: DepartureReturnButtonState.Return,
        datesButtonActiveState: DepartureReturnButtonState.Return,
        isValid: false
      };
    } else if (!selectedDates?.start) {
      result = {
        datesButtonState: DepartureReturnButtonState.Departure,
        datesButtonActiveState: DepartureReturnButtonState.Departure,
        isValid: false
      };
    }

    return result;
  }

  public getStartDateLimit(currentRestrictions: ProductInfoInterface, startDate: Date | null): number {
    const endDate = new Date(currentRestrictions?.MaxExposurePolicyEndDate);
    const startDateLimit = startDate
      ? Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
      : 0;
    return startDateLimit;
  }

  public getProductInfo(): Observable<CalendarRestrictionsResponse> {
    const body = {
      siteID: 9,
      isDomestic: this.destinationsStore.isDomestic(),
      assosiationID: this.agentStore.organizationId()
    };
    return this.restApiService.post(`products/getProducts`, body).pipe(
      map((response) => {
        const res = response as CalendarRestrictionsResponse;
        const maxValues = this.getMaxValues(res);

        return {
          statusCode: 200,
          status: 200,
          error: '',
          data: [
            {
              MaxExposure: maxValues.MaxExposure,
              MaxPolicyPeriodDays: maxValues.MaxPolicyPeriodDays,
              MaxExposurePolicyEndDate: maxValues.MaxExposurePolicyEndDate,
              Id: maxValues.Id,
              Name: maxValues.Name,
              IsAnnual: maxValues.IsAnnual,
              IsDomestic: maxValues.IsDomestic,
              HasCoupon: maxValues.HasCoupon,
              AmtTripDurations: maxValues.AmtTripDurations
            }
          ]
        };
      })
    );
  }

  private getMaxValues(response: CalendarRestrictionsResponse): ProductInfoInterface {
    return response.data.reduce((acc: ProductInfoInterface, product: ProductInfoInterface) => {
      acc.MaxExposure = Math.max(acc.MaxExposure, product.MaxExposure);
      acc.MaxPolicyPeriodDays = Math.max(acc.MaxPolicyPeriodDays, product.MaxPolicyPeriodDays);
      acc.MaxExposurePolicyEndDate =
        new Date(acc.MaxExposurePolicyEndDate) > new Date(product.MaxExposurePolicyEndDate)
          ? acc.MaxExposurePolicyEndDate
          : product.MaxExposurePolicyEndDate;
      acc.Id = response.data[0].Id;
      acc.Name = response.data[0].Name;
      acc.IsAnnual = false;
      acc.IsDomestic = false;
      acc.HasCoupon = false;
      acc.AmtTripDurations = [];
      return acc;
    });
  }
}
